import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CustomerHoldingsModal } from '@/components/CustomerHoldingsModal';
import { api } from '@/lib/api';
import { useAuth } from '@/store/auth';
import type {
  Appointment,
  Certification,
  Complaint,
  CoverageDay,
  Customer,
  InventoryItem,
  Staff,
  StaffRequest,
  Treatment,
} from '@/lib/types';
import { Card, PageHeader, Pill, Stat, cellStyle, thStyle } from '@/components/ui';
import { Icon, I, IconKey } from '@/components/icons';
import { ntd, STAGE_LABEL, dt } from '@/lib/format';

const ARRIVED = new Set(['CHECKIN', 'CONSULT', 'TREATING', 'DONE']);
const sameDay = (iso: string, ref: Date) => new Date(iso).toDateString() === ref.toDateString();

export function DashboardPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN' || user?.role === 'STORE_MANAGER';
  const now = useMemo(() => new Date(), []);
  const [detail, setDetail] = useState<Customer | null>(null);

  const appts = useQuery({ queryKey: ['appointments'], queryFn: () => api.get<Appointment[]>('/appointments') });
  const customers = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers') });
  const treatments = useQuery({ queryKey: ['treatments'], queryFn: () => api.get<Treatment[]>('/catalog/treatments') });
  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.get<Staff[]>('/catalog/staff') });
  const revenue = useQuery({ queryKey: ['fin-revenue'], enabled: isAdmin, retry: false, queryFn: () => api.get<{ netOperationalCents: number; consumedCents: number }>('/finance/revenue') });
  const kpis = useQuery({ queryKey: ['fin-kpis'], queryFn: () => api.get<{ customers: number; appointments: number; byStage: Record<string, number> }>('/finance/kpis') });
  const coverage = useQuery({ queryKey: ['coverage'], queryFn: () => api.get<CoverageDay[]>('/scheduling/coverage') });
  const requests = useQuery({ queryKey: ['sched-requests'], queryFn: () => api.get<StaffRequest[]>('/scheduling/requests') });
  const certs = useQuery({ queryKey: ['certs-expired'], queryFn: () => api.get<Certification[]>('/scheduling/certs/expired') });
  const complaints = useQuery({ queryKey: ['complaints'], queryFn: () => api.get<Complaint[]>('/compliance/complaints') });
  const lowStock = useQuery({ queryKey: ['inv-low'], queryFn: () => api.get<InventoryItem[]>('/inventory/items/low') });

  const apptList = appts.data ?? [];
  const customerName = (id: string) => customers.data?.find((c) => c.id === id)?.name ?? '—';
  const treatmentName = (id: string) => treatments.data?.find((t) => t.id === id)?.name ?? '—';
  const staffName = (id: string) => staff.data?.find((s) => s.id === id)?.name ?? '—';

  const todayAppts = apptList.filter((a) => sameDay(a.startAt, now) && a.stage !== 'CANCELLED');
  const arrivedToday = todayAppts.filter((a) => ARRIVED.has(a.stage)).length;
  const arrivalRate = todayAppts.length > 0 ? Math.round((arrivedToday / todayAppts.length) * 100) : null;

  // 今日優先；若今日無，回退顯示最近未來預約
  const upcoming = apptList
    .filter((a) => a.stage !== 'CANCELLED' && new Date(a.startAt).getTime() >= now.getTime() - 86400000)
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())
    .slice(0, 8);
  const board = todayAppts.length > 0 ? todayAppts : upcoming;

  const pendingReq = (requests.data ?? []).filter((r) => r.status === 'PENDING').length;
  const openComplaints = (complaints.data ?? []).filter((c) => c.status !== 'RESOLVED').length;
  const futureUnderstaffed = (coverage.data ?? []).filter((c) => c.understaffed && new Date(c.date).getTime() >= now.getTime() - 86400000);

  const byStage = kpis.data?.byStage ?? {};

  const todos: { label: string; count: number; tone: 'danger' | 'warn' | 'primary'; to: string; icon: IconKey }[] = [
    { label: '低庫存 / 斷貨耗材', count: lowStock.data?.length ?? 0, tone: 'danger', to: '/inventory', icon: 'layers' },
    { label: '證照已過期', count: certs.data?.length ?? 0, tone: 'danger', to: '/scheduling', icon: 'shield' },
    { label: '待審排班申請', count: pendingReq, tone: 'warn', to: '/scheduling', icon: 'clock' },
    { label: '處理中客訴', count: openComplaints, tone: 'warn', to: '/compliance', icon: 'alert' },
    { label: '人力不足日', count: futureUnderstaffed.length, tone: 'primary', to: '/scheduling', icon: 'users' },
  ];

  return (
    <div>
      <PageHeader title="今日調度看板" subtitle={`${now.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })} · 全院營運總覽`} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="今日預約" value={String(todayAppts.length)} hint={`到診 ${arrivedToday} 筆`} tone="primary" />
        <Stat label="今日到診率" value={arrivalRate === null ? '—' : `${arrivalRate}%`} tone={arrivalRate !== null && arrivalRate >= 70 ? 'success' : 'warn'} />
        {isAdmin && <Stat label="本月淨營業額" value={ntd(revenue.data?.netOperationalCents ?? 0)} hint="雙軌：排除遷移資料" tone="wallet" />}
        <Stat label="顧客總數" value={String(kpis.data?.customers ?? customers.data?.length ?? 0)} tone="ink" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16 }}>
        {/* 今日 / 近期預約 */}
        <Card title={todayAppts.length > 0 ? '今日預約排程' : '近期預約排程'} icon={<Icon d={I.cal} size={14} style={{ color: 'var(--primary)' }} />} right={<button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => nav('/booking')}>前往預約引擎 →</button>}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thStyle}>時間</th><th style={thStyle}>顧客</th><th style={thStyle}>療程</th><th style={thStyle}>醫師</th><th style={thStyle}>狀態</th>
            </tr></thead>
            <tbody>
              {board.map((a) => (
                <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => nav('/booking')}>
                  <td style={cellStyle} className="num">{dt(a.startAt)}</td>
                  <td style={cellStyle}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const c = customers.data?.find((x) => x.id === a.customerId);
                        if (c) setDetail(c);
                      }}
                      title="檢視儲值金 / 堂票"
                      style={{
                        background: 'transparent',
                        border: 0,
                        padding: 0,
                        font: 'inherit',
                        fontWeight: 600,
                        color: 'var(--primary-deep, var(--ink))',
                        cursor: 'pointer',
                      }}
                    >
                      {customerName(a.customerId)}
                    </button>
                  </td>
                  <td style={cellStyle}>{treatmentName(a.treatmentId)}</td>
                  <td style={cellStyle}>{staffName(a.doctorId)}</td>
                  <td style={cellStyle}><StagePill stage={a.stage} /></td>
                </tr>
              ))}
              {board.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={5}>今日與近期皆無預約</td></tr>}
            </tbody>
          </table>
        </Card>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 待辦提醒 */}
          <Card title="待辦提醒" icon={<Icon d={I.bell} size={14} style={{ color: 'var(--warn)' }} />}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {todos.map((t) => (
                <button key={t.label} onClick={() => nav(t.to)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 8, border: '1px solid var(--line)', background: t.count > 0 ? `var(--${t.tone}-bg)` : 'var(--surface)', cursor: 'pointer', textAlign: 'left' }}>
                  <Icon d={I[t.icon]} size={15} style={{ color: t.count > 0 ? `var(--${t.tone})` : 'var(--ink-4)' }} />
                  <span style={{ flex: 1, fontSize: 12.5, color: 'var(--ink)' }}>{t.label}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: t.count > 0 ? `var(--${t.tone})` : 'var(--ink-4)' }} className="num">{t.count}</span>
                </button>
              ))}
            </div>
          </Card>

          {/* 預約階段分布 */}
          <Card title="預約階段分布" icon={<Icon d={I.chart} size={14} style={{ color: 'var(--primary)' }} />}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {Object.entries(byStage).length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>尚無資料</div>}
              {Object.entries(byStage).map(([stage, n]) => {
                const total = Object.values(byStage).reduce((s, v) => s + v, 0) || 1;
                return (
                  <div key={stage} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 56, fontSize: 11.5, color: 'var(--ink-2)' }}>{STAGE_LABEL[stage] ?? stage}</span>
                    <div style={{ flex: 1, height: 8, background: 'var(--surface-2)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{ width: `${(n / total) * 100}%`, height: '100%', background: 'var(--primary)' }} />
                    </div>
                    <span style={{ width: 22, textAlign: 'right', fontSize: 12, fontWeight: 600 }} className="num">{n}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      {/* 人力不足提醒 */}
      {futureUnderstaffed.length > 0 && (
        <Card title="人力配置警示" icon={<Icon d={I.users} size={14} style={{ color: 'var(--danger)' }} />} right={<button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => nav('/scheduling')}>調整排班 →</button>}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={thStyle}>日期</th><th style={thStyle}>醫師</th><th style={thStyle}>顧問</th><th style={thStyle}>原因</th></tr></thead>
            <tbody>
              {futureUnderstaffed.slice(0, 6).map((c) => (
                <tr key={c.date}>
                  <td style={cellStyle} className="num">{c.date}</td>
                  <td style={cellStyle}>{c.doctors}</td>
                  <td style={cellStyle}>{c.consultants}</td>
                  <td style={cellStyle}>{c.reasons.map((r) => <Pill key={r} bg="var(--danger-bg)" color="var(--danger)" style={{ marginRight: 4 }}>{r}</Pill>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <CustomerHoldingsModal customer={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function StagePill({ stage }: { stage: string }) {
  const map: Record<string, { bg: string; color: string }> = {
    BOOKED: { bg: 'var(--surface-2)', color: 'var(--ink-2)' },
    CHECKIN: { bg: 'var(--warn-bg)', color: 'var(--warn)' },
    CONSULT: { bg: 'var(--primary-tint)', color: 'var(--primary-deep)' },
    TREATING: { bg: 'var(--primary-tint)', color: 'var(--primary-deep)' },
    DONE: { bg: 'var(--success-bg)', color: 'var(--success)' },
    CANCELLED: { bg: 'var(--danger-bg)', color: 'var(--danger)' },
  };
  const s = map[stage] ?? map.BOOKED;
  return <Pill bg={s.bg} color={s.color}>{STAGE_LABEL[stage] ?? stage}</Pill>;
}
